import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/products/shared/services/supabase/client';
import { useAuth } from '@/products/shared/state/auth-context';
import {
  isUserProductAccessAllowed,
  type UserProductAccessKey,
  USER_PRODUCT_ACCESS_TABLE,
} from '@/products/shared/utils/user-product-access';

export const currentUserProductAccessQueryKey = (
  tenantId: number | null,
  userId?: string | null,
) => ['user-product-access', tenantId, userId ?? 'self', 'all'] as const;

export const tenantProductAccessQueryKey = (tenantId: number | null) =>
  ['user-product-access', tenantId, 'tenant', 'all'] as const;

type AccessRow = {
  user_id: string;
  product: string;
  enabled: boolean;
};

/** userId → product → enabled override (missing product = default allow) */
export type UserProductAccessMatrix = Record<string, Record<string, boolean>>;

const buildMatrix = (rows: AccessRow[]): UserProductAccessMatrix => {
  const matrix: UserProductAccessMatrix = {};
  for (const row of rows) {
    if (!matrix[row.user_id]) {
      matrix[row.user_id] = {};
    }
    matrix[row.user_id][row.product] = row.enabled;
  }
  return matrix;
};

/**
 * Current user's product overrides. Missing product keys default to allowed.
 */
export const useCurrentUserProductAccess = () => {
  const { tenantId, user } = useAuth();
  const userId = user?.id ?? null;

  const query = useQuery({
    queryKey: currentUserProductAccessQueryKey(tenantId, userId),
    enabled: Boolean(tenantId && userId),
    queryFn: async (): Promise<Record<string, boolean>> => {
      if (!tenantId || !userId) {
        return {};
      }

      const { data, error } = await (supabase as any)
        .from(USER_PRODUCT_ACCESS_TABLE)
        .select('product, enabled')
        .eq('tenant_id', tenantId)
        .eq('user_id', userId);

      if (error) {
        throw error;
      }

      const map: Record<string, boolean> = {};
      for (const row of (data ?? []) as Array<{ product: string; enabled: boolean }>) {
        map[row.product] = row.enabled;
      }
      return map;
    },
  });

  const accessByProduct = query.data ?? {};

  const hasAccess = (product: UserProductAccessKey): boolean =>
    isUserProductAccessAllowed(accessByProduct[product]);

  return {
    accessByProduct,
    hasAccess,
    isLoading: Boolean(tenantId && userId) && query.isLoading,
    error: query.error,
  };
};

/** @deprecated Prefer useCurrentUserProductAccess for multi-product checks */
export const useHasUserProductAccess = (product: UserProductAccessKey) => {
  const { hasAccess, isLoading, error } = useCurrentUserProductAccess();
  return {
    hasAccess: hasAccess(product),
    isLoading,
    error,
  };
};

/**
 * Tenant-wide matrix of user_id → product → enabled for Settings → Users.
 */
export const useTenantUserProductAccessMatrix = () => {
  const { tenantId } = useAuth();

  const query = useQuery({
    queryKey: tenantProductAccessQueryKey(tenantId),
    enabled: Boolean(tenantId),
    queryFn: async (): Promise<UserProductAccessMatrix> => {
      if (!tenantId) {
        return {};
      }

      const { data, error } = await (supabase as any)
        .from(USER_PRODUCT_ACCESS_TABLE)
        .select('user_id, product, enabled')
        .eq('tenant_id', tenantId);

      if (error) {
        throw error;
      }

      return buildMatrix((data ?? []) as AccessRow[]);
    },
  });

  return {
    accessMatrix: query.data ?? {},
    isLoading: Boolean(tenantId) && query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

type SetAccessArgs = {
  userId: string;
  product: UserProductAccessKey;
  enabled: boolean;
};

export const useSetUserProductAccess = () => {
  const { tenantId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, product, enabled }: SetAccessArgs) => {
      if (!tenantId) {
        throw new Error('Tenant information not available');
      }
      if (!userId) {
        throw new Error('User is required');
      }

      const { error } = await (supabase as any)
        .from(USER_PRODUCT_ACCESS_TABLE)
        .upsert(
          {
            tenant_id: tenantId,
            user_id: userId,
            product,
            enabled,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'tenant_id,user_id,product' },
        );

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['user-product-access', tenantId],
      });
    },
  });
};
